import { useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { getBackendOrigin, getSocketIoPath } from '../lib/socket';

export interface TranscriptMessage {
  speaker: 'user' | 'assistant';
  text: string;
  isFinal: boolean;
  timestamp: Date;
}

export interface CallEvent {
  type: 'call_started' | 'call_ended';
  callId: string;
  fromNumber?: string;
  timestamp: Date;
}

export interface UseRealtimeTranscriptReturn {
  messages: TranscriptMessage[];
  isConnected: boolean;
  isTyping: boolean;
  callEvents: CallEvent[];
  joinRoom: (callId: string) => void;
  clearMessages: () => void;
  disconnect: () => void;
}

const backendOrigin = getBackendOrigin();
const socketPath = getSocketIoPath();

export function useRealtimeTranscript(autoConnect = true): UseRealtimeTranscriptReturn {
  const [messages, setMessages] = useState<TranscriptMessage[]>([]);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [callEvents, setCallEvents] = useState<CallEvent[]>([]);
  
  const socketRef = useRef<Socket | null>(null);
  const currentAssistantMessageIndexRef = useRef<number | null>(null);

  useEffect(() => {
    if (!autoConnect) return;

    // Initialize socket connection
    const socket = io(backendOrigin, {
      transports: ['websocket', 'polling'],
      path: socketPath,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('✅ Connected to WebSocket Server (Transcript)', {
        url: backendOrigin,
        path: socketPath,
        id: socket.id,
      });
      setIsConnected(true);
    });

    socket.on('connect_error', (error) => {
      console.error('❌ Connection Error:', error);
      setIsConnected(false);
    });

    socket.on('disconnect', () => {
      console.log('🔌 Disconnected from WebSocket Server');
      setIsConnected(false);
    });

    socket.on('call_started', (data: { call_id: string; from_number?: string }) => {
    //   console.log('📞 Call started:', data);
      
      const callEvent: CallEvent = {
        type: 'call_started',
        callId: data.call_id,
        fromNumber: data.from_number,
        timestamp: new Date(),
      };

      setCallEvents(prev => [...prev, callEvent]);

      // Auto-join the call room
      if (data.call_id) {
        socket.emit('join', { room: `call_${data.call_id}` });
        console.log(`Auto-joining room: call_${data.call_id}`);
      }
    });

    socket.on('transcript_update', (data: { speaker: 'user' | 'assistant'; text: string; is_final: boolean }) => {
    //   console.log('📝 Transcript update:', data);
      
      // Validate incoming data
      if (!data || !data.speaker || typeof data.text !== 'string') {
        console.error('Invalid transcript update data:', data);
        return;
      }
      
      const newMessage: TranscriptMessage = {
        speaker: data.speaker,
        text: data.text || '', // Ensure text is never undefined
        isFinal: data.is_final || false,
        timestamp: new Date(),
      };

      setMessages(prevMessages => {
        if (data.speaker === 'assistant' && !data.is_final) {
          setIsTyping(true);
          
          if (currentAssistantMessageIndexRef.current !== null) {
            const updatedMessages = [...prevMessages];
            const lastMessage = updatedMessages[currentAssistantMessageIndexRef.current];
            
            if (lastMessage && typeof lastMessage.text === 'string') {
              // Append to existing message
              updatedMessages[currentAssistantMessageIndexRef.current] = {
                ...lastMessage,
                text: lastMessage.text + data.text,
                isFinal: false,
              };
              
              return updatedMessages;
            } else {
              console.warn('Invalid lastMessage, creating new message:', lastMessage);
              currentAssistantMessageIndexRef.current = prevMessages.length;
              return [...prevMessages, newMessage];
            }
          } else {
            currentAssistantMessageIndexRef.current = prevMessages.length;
            return [...prevMessages, newMessage];
          }
        }

        // If it's final or user message
        if (data.is_final) {
          setIsTyping(false);
          currentAssistantMessageIndexRef.current = null;
        }

        // For user messages or final assistant messages
        if (data.speaker === 'user' || data.is_final) {
          return [...prevMessages, newMessage];
        }

        return prevMessages;
      });
    });

    socket.on('call_ended', (data: { call_id?: string }) => {
    //   console.log('🔴 Call ended:', data);
      
      const callEvent: CallEvent = {
        type: 'call_ended',
        callId: data.call_id || 'unknown',
        timestamp: new Date(),
      };
      
      setCallEvents(prev => [...prev, callEvent]);
      setIsTyping(false);
      currentAssistantMessageIndexRef.current = null;
    });

    // Cleanup on unmount
    return () => {
      socket.disconnect();
    };
  }, [autoConnect]);

  const joinRoom = useCallback((callId: string) => {
    if (socketRef.current) {
      const room = `call_${callId}`;
      socketRef.current.emit('join', { room });
      console.log(`Joining room: ${room}`);
    }
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setCallEvents([]);
    currentAssistantMessageIndexRef.current = null;
    setIsTyping(false);
  }, []);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  return {
    messages,
    isConnected,
    isTyping,
    callEvents,
    joinRoom,
    clearMessages,
    disconnect,
  };
}
